<script setup lang="ts">
import './assets/style.scss'
import { useRoute, useRouter } from 'vue-router'
import { useInventoryObjectTypesModel } from './../model/model'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { InventObjectTypesFilter } from '@/features/Inventory/ObjectTypesFilter'
import { ObjectTypeGroupCard } from '@/features/Inventory/ObjectTypes'
import { useGroupMain } from '@/entities/Group/Main'
import { useAuth } from '@/entities/Auth'
import { pluralizeCount } from '@/shared/lib'

const route = useRoute()
const router = useRouter()
const model = useInventoryObjectTypesModel()
const groupMain = useGroupMain()
const auth = useAuth()

const objectId = computed(() => String(route.params.object_id))
const section = computed(() => route.params.section as string)
const { isPending, refetch } = model.loadData(objectId)

/* Выбранный в фильтре тип — карточки фильтруются на клиенте */
const selectedType = ref(0)

/* Обновляем данные при смене типа объекта */
watch(objectId, () => refetch())

/* Фильтр по is_system в зависимости от секции; грузим только payload с типами */
watch(
  section,
  (s) => {
    const isSystem = s === 'manuals' ? 1 : s === 'passports-config' ? 0 : undefined
    model.filter = {
      search: '',
      object_type: 0,
      is_system: isSystem,
      page: 1,
      per_page: 1,
    }
    selectedType.value = 0
    refetch()
  },
  { immediate: true },
)

/* Разрешенные типы из состояния группы */
const allowedObjectTypeIds = computed<number[] | null>(() => groupMain.group?.object_types ?? null)

/* Список типов для фильтра селекта с учетом ограничений группы и прав */
const filteredObjectTypes = computed(() => {
  const currentSection = section.value
  const baseTypes = model.objectTypes

  const allowedIds = allowedObjectTypeIds.value

  let result = baseTypes.filter((type: any) => {
    const typeId = Number(type.id)
    // Скрываем типы, на которые у пользователя нет права на чтение
    return auth.hasTypePermission(typeId, 'read')
  })

  if (currentSection === 'passports-config' && Array.isArray(allowedIds) && allowedIds.length > 0) {
    result = result.filter((type: any) => {
      const typeId = Number(type.id)
      return allowedIds.includes(typeId)
    })
  }

  return result
})

/* Карточки: только типы с родителем и элементами, с учетом выбранного типа */
const cardTypes = computed(() =>
  filteredObjectTypes.value.filter((type: any) => {
    if (type.parent_id == null || !type.elements_count) return false
    return selectedType.value > 0 ? Number(type.id) === selectedType.value : true
  }),
)

/* Общее число элементов во всех видимых карточках */
const totalElements = computed(() =>
  cardTypes.value.reduce((sum, type: any) => sum + (type.elements_count ?? 0), 0),
)

/** Подпись счетчика типов и элементов в строке фильтров */
const totalLabel = computed(() => {
  const types = pluralizeCount('itf.card_types_count', cardTypes.value.length)
  const elements = pluralizeCount('itf.card_elements_count', totalElements.value)
  return `${types} (${elements})`
})

/* Ширина первой карточки — центральный блок фильтра подгоняем под неё */
const cardsRef = ref<HTMLElement | null>(null)
const cardWidth = ref(620)

const updateCardWidth = () => {
  const firstCard = cardsRef.value?.querySelector('.object-type-card')
  if (firstCard) cardWidth.value = Math.round(firstCard.getBoundingClientRect().width)
}

let cardsObserver: ResizeObserver | null = null

onMounted(() => {
  updateCardWidth()
  cardsObserver = new ResizeObserver(updateCardWidth)
  if (cardsRef.value) cardsObserver.observe(cardsRef.value)
})

onBeforeUnmount(() => cardsObserver?.disconnect())

watch(cardTypes, () => setTimeout(updateCardWidth))

/**
 * Функция применения фильтра — клиентская фильтрация по типу
 * @param data
 */
const submitHandler = (data: { type: number }) => {
  selectedType.value = data.type
}

/* Вычисляем ссылку для создания инвентаризационного типа */
const createLink = computed(() => {
  const query: Record<string, string | number> = {
    object_type: selectedType.value,
  }
  if (model.filter.is_system !== undefined) {
    query.is_system = model.filter.is_system
  }
  return router.resolve({
    name: 'objects.groups.admin',
    params: {
      ...route.params,
      action: 'create',
      entity_id: null,
    },
    query,
  })
})

const canShowAddButton = computed(() => {
  if (selectedType.value > 0) {
    return auth.hasTypePermission(selectedType.value, 'create')
  }
  // Если тип не выбран — показываем, если есть create хотя бы для одного типа текущей секции
  const sectionTypes = filteredObjectTypes.value
  return sectionTypes.some((t: any) => auth.hasTypePermission(Number(t.id), 'create'))
})
</script>

<template>
  <InventObjectTypesFilter
    :types="filteredObjectTypes"
    :section="section"
    :center-width="cardWidth"
    @submit="submitHandler"
  >
    <template #prepend>
      <span class="object-type-cards__total">{{ totalLabel }}</span>
    </template>

    <template #append>
      <VBtn v-if="canShowAddButton" :to="createLink">{{ $t('itf.add') }}</VBtn>
    </template>
  </InventObjectTypesFilter>

  <VProgressLinear v-if="isPending" indeterminate color="primary" />

  <div ref="cardsRef" class="object-type-cards">
    <ObjectTypeGroupCard
      v-for="type in cardTypes"
      :key="type.id"
      :type="type"
      :object-id="objectId"
      :section="section"
      @changed="refetch"
    />
  </div>

  <div v-if="!isPending && !cardTypes.length" class="object-type-cards__empty">
    {{ $t('itf.empty_search') }}
  </div>
</template>
